import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  validateTicket,
  TICKET_CONSTANTS,
} from "../../Validators/ticket.validator";
import Compressor from "compressorjs";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile } from "@ffmpeg/util";

const CATEGORIES = [
  { value: "user_data", label: "User Data" },
  { value: "recharge", label: "Recharge" },
  { value: "redeem", label: "Redeem" },
  { value: "game_coins", label: "Game Coins" },
  { value: "daily_coins", label: "Daily Coins" },
  { value: "account", label: "Account" },
  { value: "reports", label: "Reports" },
  { value: "profile", label: "Profile" },
  { value: "marketing", label: "Marketing" },
  { value: "others", label: "Others" },
];

const MAX_SIZE = 512 * 1024; // 512 KB

const NewTicketDialog = ({ open, onClose, onSubmit }) => {
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [fileError, setFileError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ffmpeg] = useState(new FFmpeg());
  const [ffmpegReady, setFfmpegReady] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);

  // Load ffmpeg once
  useEffect(() => {
    (async () => {
      try {
        await ffmpeg.load();
        setFfmpegReady(true);
      } catch (error) {
        console.error("Failed to load FFmpeg:", error);
      }
    })();
  }, [ffmpeg]);

  // Get video duration helper
  const getVideoDuration = (file) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve(video.duration);
      };
      video.onerror = () => reject("Failed to load video metadata");
      video.src = URL.createObjectURL(file);
    });
  };

  // Image compressor using CompressorJS
  const compressImage = async (file) => {
    return new Promise((resolve, reject) => {
      let quality = 0.6;
      const attempt = () => {
        new Compressor(file, {
          quality,
          convertTypes: ["image/jpeg"],
          success(result) {
            if (result.size <= MAX_SIZE) {
              resolve(result);
            } else if (quality <= 0.1) {
              reject(new Error("Please try a smaller image."));
            } else {
              quality -= 0.1;
              attempt();
            }
          },
          error(err) {
            reject(err);
          },
        });
      };
      attempt();
    });
  };

  const compressVideo = async (file) => {
    const duration = await getVideoDuration(file);
    const MAX_SIZE = 512 * 1024; // 512KB
    const targetBits = MAX_SIZE * 8;

    let calculatedBitrate = Math.floor(targetBits / duration / 1000);
    const MIN_480P_BITRATE = 300;

    if (calculatedBitrate < MIN_480P_BITRATE) {
      throw new Error("Please try a smaller file");
    }

    const bitrate = calculatedBitrate;

    ffmpeg.writeFile("input.mp4", await fetchFile(file));
    await ffmpeg.exec([
      "-i",
      "input.mp4",
      "-vf",
      "scale=854:480",
      "-b:v",
      `${bitrate}k`,
      "-maxrate",
      `${bitrate}k`,
      "-bufsize",
      `${bitrate * 2}k`,
      "-preset",
      "veryfast",
      "-crf",
      "28",
      "output.mp4",
    ]);

    const data = await ffmpeg.readFile("output.mp4");
    const blob = new Blob([data.buffer], { type: "video/mp4" });

    if (blob.size > MAX_SIZE) {
      throw new Error("Please try a smaller file");
    }

    return blob;
  };

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files[0];
    setFileError("");

    if (!selectedFile) return;

    if (selectedFile.size > TICKET_CONSTANTS.MAX_FILE_SIZE) {
      setFileError("File size must be less than 25MB");
      return;
    }

    const isImage = TICKET_CONSTANTS.ALLOWED_IMAGE_TYPES.includes(
      selectedFile.type
    );
    const isVideo = TICKET_CONSTANTS.ALLOWED_VIDEO_TYPES.includes(
      selectedFile.type
    );

    if (!isImage && !isVideo) {
      setFileError("Only image and video files are allowed");
      return;
    }

    if (selectedFile.size > MAX_SIZE) {
      setIsCompressing(true);
      try {
        let compressedFile;
        if (isImage) {
          compressedFile = await compressImage(selectedFile);
          compressedFile = new File([compressedFile], selectedFile.name, {
            type: compressedFile.type,
            lastModified: Date.now(),
          });
        } else if (isVideo) {
          if (!ffmpegReady) {
            setFileError("Video compressor is still loading. Please wait...");
            setIsCompressing(false);
            return;
          }
          compressedFile = await compressVideo(selectedFile);
          compressedFile = new File(
            [compressedFile],
            selectedFile.name.replace(/\.[^/.]+$/, ".mp4"),
            {
              type: "video/mp4",
              lastModified: Date.now(),
            }
          );
        }
        setFile(compressedFile);
      } catch (error) {
        console.error("Compression error:", error);
        setFileError("Please try a smaller file.");
      } finally {
        setIsCompressing(false);
      }
    } else {
      setFile(selectedFile);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setFileError("");
  };

  const validateForm = () => {
    const ticketData = {
      category,
      description,
      file,
    };

    const validation = validateTicket(ticketData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      if (validation.errors.file) {
        setFileError(validation.errors.file);
      }
    }
    return validation.isValid;
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const ticketData = {
        category,
        description: description.trim(),
      };

      if (file) {
        try {
          const base64Data = await fileToBase64(file);
          ticketData.fileBase64 = base64Data;
          ticketData.fileName = file.name;
          ticketData.fileType = file.type;
          ticketData.fileSize = file.size;
        } catch (error) {
          console.error("Error converting file to base64:", error);
          setFileError("Failed to process file. Please try again.");
          setIsSubmitting(false);
          return;
        }
      }

      await onSubmit(ticketData);

      setCategory("");
      setDescription("");
      setFile(null);
      setErrors({});
      setFileError("");
      onClose();
    } catch (error) {
      console.error("Error submitting ticket:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setCategory("");
      setDescription("");
      setFile(null);
      setErrors({});
      setFileError("");
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          bgcolor: "#1A1A1A",
          color: "#FFF",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
          color: "#FFF",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Create New Ticket
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          disabled={isSubmitting}
          sx={{
            color: "#FFF",
            "&:hover": {
              transform: "rotate(90deg)",
              transition: "transform 0.3s",
              bgcolor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          borderColor: "rgba(255, 239, 153, 0.20)",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Box>
            <TextField
              select
              fullWidth
              label="Category"
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setErrors((prev) => ({ ...prev, category: "" }));
              }}
              error={!!errors.category}
              helperText={errors.category}
              required
              disabled={isSubmitting}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.5,
                  color: "#FFF",
                  "& fieldset": {
                    borderColor: "rgba(255, 239, 153, 0.20)",
                  },
                  "&:hover fieldset": {
                    borderColor: "#FFD700",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#FFD700",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "rgba(255, 255, 255, 0.7)",
                  fontSize: "0.75em",
                  "&.Mui-focused": {
                    color: "#FFD700",
                  },
                },
                "& .MuiFormHelperText-root": {
                  color: "rgba(255, 255, 255, 0.7)",
                },
                "& .MuiSelect-icon": {
                  color: "#FFD700",
                },
              }}
            >
              {CATEGORIES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box>
            <TextField
              fullWidth
              multiline
              rows={5}
              label="Description"
              placeholder="Please describe your issue in detail..."
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                setErrors((prev) => ({ ...prev, description: "" }));
              }}
              error={!!errors.description}
              helperText={errors.description || `${description.length} characters`}
              required
              disabled={isSubmitting}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.5,
                  color: "#FFF",
                  "& fieldset": {
                    borderColor: "rgba(255, 239, 153, 0.20)",
                  },
                  "&:hover fieldset": {
                    borderColor: "#FFD700",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#FFD700",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "rgba(255, 255, 255, 0.7)",
                  fontSize: "0.75em",
                  "&.Mui-focused": {
                    color: "#FFD700",
                  },
                },
                "& .MuiFormHelperText-root": {
                  color: "rgba(255, 255, 255, 0.7)",
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "rgba(255, 255, 255, 0.5)",
                  opacity: 1,
                },
              }}
            />
          </Box>

          <Box>
            <Typography
              variant="body2"
              sx={{ mb: 1, color: "rgba(255, 255, 255, 0.7)", fontWeight: 500 }}
            >
              Attach Screenshot (Optional)
            </Typography>
            {!file ? (
              <Button
                variant="outlined"
                component="label"
                startIcon={
                  isCompressing ? (
                    <CircularProgress size={20} sx={{ color: "#FFD700" }} />
                  ) : (
                    <AttachFileIcon />
                  )
                }
                disabled={isSubmitting || isCompressing}
                sx={{
                  borderRadius: 1.5,
                  textTransform: "none",
                  borderStyle: "dashed",
                  py: 1.5,
                  width: "100%",
                  color: "#FFF",
                  borderColor: "rgba(255, 239, 153, 0.20)",
                  "&:hover": {
                    borderStyle: "dashed",
                    borderColor: "#FFD700",
                    bgcolor: "rgba(255, 215, 0, 0.05)",
                  },
                }}
              >
                {isCompressing ? "Compressing..." : "Upload Image/Video"}
                <input
                  type="file"
                  hidden
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/bmp,video/mp4,video/mpeg,video/quicktime,video/x-msvideo,video/webm"
                  onChange={handleFileChange}
                />
              </Button>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1.5,
                  border: "1px solid",
                  borderColor: "rgba(255, 239, 153, 0.20)",
                  borderRadius: 1.5,
                  bgcolor: "rgba(255, 215, 0, 0.05)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <AttachFileIcon fontSize="small" sx={{ color: "#FFD700" }} />
                  <Typography variant="body2" noWrap sx={{ maxWidth: 300, color: "#FFF" }}>
                    {file.name}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={handleRemoveFile}
                  disabled={isSubmitting}
                  sx={{ color: "#FF6B6B" }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            {fileError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {fileError}
              </Alert>
            )}
            <Typography
              variant="caption"
              sx={{ mt: 0.5, display: "block", color: "rgba(255, 255, 255, 0.5)" }}
            >
              Supported formats: Images (JPEG, JPG, PNG, GIF, WebP, BMP) and
              Videos (MP4, MPEG, MOV, AVI, WebM). Files will be automatically
              compressed to 512KB if larger.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid rgba(255, 239, 153, 0.20)" }}>
        <Button
          onClick={handleClose}
          disabled={isSubmitting}
          variant="outlined"
          sx={{
            textTransform: "capitalize",
            borderRadius: 1.5,
            flex: 1,
            border: "1px solid rgba(255, 239, 153, 0.20)",
            color: "#FFF",
            "&:hover": {
              border: "1px solid #fff",
              background: "none",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting}
          sx={{
            textTransform: "capitalize",
            borderRadius: 1.5,
            flex: 1,
            minWidth: 100,
            backgroundColor: "#FFD700",
            color: "#000",
            fontWeight: 500,
            "&:hover": {
              backgroundColor: "#FFA000",
            },
            "&.Mui-disabled": {
              backgroundColor: "rgba(255, 215, 0, 0.3)",
              color: "rgba(0, 0, 0, 0.5)",
            },
          }}
        >
          {isSubmitting ? (
            <CircularProgress size={24} sx={{ color: "#000" }} />
          ) : (
            "Confirm"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewTicketDialog;
